import React from 'react';

import { LeftOutlined, StopOutlined } from '@ant-design/icons';
import { useInboundAcceptance } from '@contexts/operator/InboundAcceptanceContext';
import useSmartBack from '@hooks/shared/useSmartBack';
import { Tag, Button, Typography, Space, Dropdown, Badge, Modal } from 'antd';
import { useTranslation } from 'react-i18next';

const { Title, Text } = Typography;

export default function Header() {
  const {
    inbound,
    activeReceipt,
    receiptLoading,
    finalizeReceipt,
    closeInbound,
  } = useInboundAcceptance();
  const { t } = useTranslation();
  const goBack = useSmartBack();

  if (!inbound) return null;

  const canStartReceipt = ['pending', 'receiving'].includes(inbound.status);
  const hasActiveReceipt = !!activeReceipt;

  const finalizeMenuItems = {
    items: [
      {
        key: 'finalize_receipt',
        label: t('operator.receipt.finalize_this', { defaultValue: '結案此批驗收' }),
        icon: <StopOutlined />,
      },
      {
        type: 'divider',
      },
      {
        key: 'close_inbound',
        label: t('operator.receipt.close_inbound_full'),
        danger: true,
        icon: <StopOutlined />,
      },
    ],
    onClick: ({ key }) => {
      if (key === 'finalize_receipt') {
        finalizeReceipt(false);
      } else if (key === 'close_inbound') {
        finalizeReceipt(true);
      }
    },
  };

  return (
    <div style={{ padding: '16px', borderBottom: '1px solid #f0f0f0', background: '#fff' }}>
      <Space direction="vertical" style={{ width: '100%' }} size={8}>
        {/* Row 1: Inbound info */}
        <Space style={{ justifyContent: 'space-between', width: '100%' }}>
          <Space>
            <Button icon={<LeftOutlined />} onClick={goBack} type="text" />
            <Title level={4} style={{ margin: 0 }}>
              {inbound.name}
            </Title>
            <Tag color="blue">{t(`enums.inbound.status.${inbound.status}`)}</Tag>
          </Space>
          <Space>
            <Text type="secondary">{t('models.inbound.merchant', { defaultValue: '貨主' })}: {inbound.merchant_name}</Text>
          </Space>
        </Space>

        {/* Row 2: Receipt status bar */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 16px',
          marginLeft: '40px',
          background: hasActiveReceipt ? '#f6ffed' : '#fafafa',
          borderRadius: 6,
          border: `1px solid ${hasActiveReceipt ? '#b7eb8f' : '#d9d9d9'}`
        }}>
          <Space size="large">
            {hasActiveReceipt ? (
              <>
                <Badge status="processing" color="green" />
                <Text strong>{activeReceipt.name}</Text>
                <Tag color="green">{t('operator.receipt.status_receiving', { defaultValue: '驗收中' })}</Tag>
                <Text type="secondary">
                  {t('operator.receipt.summary', {
                    defaultValue: '{{items}} 項 / {{qty}} 件',
                    items: activeReceipt.total_items || 0,
                    qty: activeReceipt.total_received_quantity || 0
                  })}
                </Text>
              </>
            ) : (
              <>
                <Badge status="default" />
                <Text type="secondary">{t('operator.receipt.no_active', { defaultValue: '無活躍驗收批次' })}</Text>
              </>
            )}

            {/* Stats */}
            <Text type="secondary" style={{ marginLeft: 16 }}>
              {t('models.inbound.total_items', { defaultValue: '總項目' })}: <Text strong>{inbound.items?.length || 0}</Text>
            </Text>
            <Text type="secondary">
              {t('models.inbound.verified_items', { defaultValue: '已驗收' })}: <Text strong style={{ color: '#52c41a' }}>
                {inbound.items?.filter(i => i.received_quantity > 0).length || 0}
              </Text>
            </Text>
          </Space>

          <Space>
            {hasActiveReceipt ? (
              <Dropdown menu={finalizeMenuItems} placement="bottomRight">
                <Button type="primary" danger loading={receiptLoading}>
                  {t('operator.receipt.finalize', { defaultValue: '結案' })}
                </Button>
              </Dropdown>
            ) : (
              canStartReceipt && (
                <Button
                  danger
                  icon={<StopOutlined />}
                  loading={receiptLoading}
                  onClick={() => {
                    Modal.confirm({
                      title: t('operator.receipt.close_inbound_confirm_title', { defaultValue: '確認結案' }),
                      content: t('operator.receipt.close_inbound_confirm_content', { defaultValue: '提前結案將關閉此入庫單的驗收流程，未驗收的項目將不再接受驗收。確定要繼續嗎？' }),
                      okText: t('common.button.confirm', { defaultValue: '確認' }),
                      okType: 'danger',
                      cancelText: t('common.button.cancel', { defaultValue: '取消' }),
                      onOk: () => closeInbound(),
                    });
                  }}
                >
                  {t('operator.receipt.close_inbound', { defaultValue: '提前結案' })}
                </Button>
              )
            )}
          </Space>
        </div>
      </Space>
    </div>
  );
}
